import { FC, useRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useDrag } from "@use-gesture/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar, DateCalendarProps } from "@mui/x-date-pickers/DateCalendar";
import { DateView } from "@mui/x-date-pickers/models";

const SWIPE_THRESHOLD = 50;

const toDayjs = (value?: string | Dayjs | null) => (value ? dayjs(value) : undefined);

type AppDateCalendarProps = Omit<DateCalendarProps, "value" | "onChange"> & {
  value?: string | Dayjs | null;
  onChange?: (date: Dayjs) => void;
  onSelected?: (date: Dayjs) => void;
  mainSelection?: DateView;
};

const AppDateCalendar: FC<AppDateCalendarProps> = ({
  className,
  value,
  onChange,
  onSelected,
  mainSelection = "day",
  slotProps,
  ...props
}) => {
  const { i18n } = useTranslation();
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const handleChange: DateCalendarProps["onChange"] = (date, _selectionState, selectedView) => {
    if (!date) return;
    onChange?.(date);
    if (selectedView === mainSelection) onSelected?.(date);
  };

  const bindSwipe = useDrag(
    ({ last, movement: [mx] }) => {
      if (!last || Math.abs(mx) < SWIPE_THRESHOLD) return;
      const goNext = i18n.dir() === "rtl" ? mx > 0 : mx < 0;
      (goNext ? nextButtonRef : previousButtonRef).current?.click();
    },
    { axis: "x", filterTaps: true, pointer: { touch: true } },
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
      <div className="w-full touch-pan-y" {...bindSwipe()}>
        <DateCalendar
          key={i18n.language}
          className={className ?? "w-full!"}
          classes={{
            viewTransitionContainer: "p-0!",
            root: "p-0!",
          }}
          sx={{
            width: "100%",
            "& .MuiPickersCalendarHeader-root": { width: "100%", padding: 0 },
            "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": {
              width: "100%",
              justifyContent: "space-between",
            },
            "& .MuiPickerDay-root, & .MuiDayCalendar-weekDayLabel": {
              flex: 1,
              maxWidth: "none",
              width: "auto",
            },
            "& .MuiYearCalendar-root": {
              width: "100%",
              columnGap: 0,
              justifyContent: "space-between",
            },
            "& .MuiYearCalendar-button": {
              flex: "0 0 33.33%",
              width: "auto",
              maxWidth: "33.33%",
            },
            "& .MuiPickerDay-root.Mui-selected": {
              backgroundColor: "var(--color-font-active) !important",
            },
            "& .MuiPickerDay-root.Mui-selected:hover, & .MuiPickerDay-root.Mui-selected:focus": {
              backgroundColor: "var(--color-font-active) !important",
            },
            "& .MuiPickerDay-root.Mui-disabled": {
              color: "var(--color-font-disabled) !important",
            },
            "& .MuiPickerDay-root.Mui-disabled.Mui-selected": {
              backgroundColor: "var(--color-background-disabled) !important",
            },
            "& .MuiYearCalendar-button.Mui-selected, & .MuiMonthCalendar-button.Mui-selected": {
              backgroundColor: "var(--color-font-active) !important",
              color: "var(--color-font-inverted) !important",
            },
            "& .MuiYearCalendar-button.Mui-selected:hover, & .MuiYearCalendar-button.Mui-selected:focus, & .MuiMonthCalendar-button.Mui-selected:hover, & .MuiMonthCalendar-button.Mui-selected:focus":
              {
                backgroundColor: "var(--color-font-active) !important",
              },
            "& .MuiYearCalendar-button.Mui-disabled, & .MuiMonthCalendar-button.Mui-disabled": {
              color: "var(--color-font-disabled) !important",
            },
          }}
          {...props}
          value={toDayjs(value) ?? null}
          onChange={handleChange}
          slotProps={{
            ...slotProps,
            previousIconButton: { ...slotProps?.previousIconButton, ref: previousButtonRef },
            nextIconButton: { ...slotProps?.nextIconButton, ref: nextButtonRef },
          }}
        />
      </div>
    </LocalizationProvider>
  );
};

export default AppDateCalendar;
